import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import { BilWeb, DateAsString, DrivstoffWeb, Eier, StatusWeb, ValgtWeb } from '../../generated-types';
import { useDeleteBil, useKjoeretoey, useUpdateBil } from '../../hooks/useBil';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import BilerValg from './bilerValg';
import Drivstoff from './drivstoff';
import Kjoeretoey from './kjoeretoey';
import Leaset from './leaset';

const newBil = {
    bilId: null,
    drivstoff: DrivstoffWeb.UKJENT,
    erLeaset: ValgtWeb.IKKE_VALGT,
    leasingUtgift: null,
    erLagtTilManuelt: true,
    eier: null,
    merke: null,
    modell: null,
    registreringsnummer: null,
    registrertFoersteGangNorge: null,
};

export type BilFormData = {
    harBil: ValgtWeb;
    bil: {
        bilId: string | null;
        drivstoff: DrivstoffWeb;
        erLeaset: ValgtWeb;
        leasingUtgift: number | null;
        erLagtTilManuelt: boolean;
        eier: Eier | null;
        merke: string | null;
        modell: string | null;
        registreringsnummer: string | null;
        registrertFoersteGangNorge: DateAsString | null;
    }[];
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Biler: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const { data: featureToggles } = useFeatureToggle();
    const { utvideDatagrunnlag } = featureToggles || {};

    const navigate = useNavigate();
    const { biler, button } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: bilContainer, isPending } = useKjoeretoey(soeknadstype);
    const methods = useForm<BilFormData>({ defaultValues: { harBil: ValgtWeb.IKKE_VALGT, bil: [] } });
    const {
        register,
        setValue,
        control,
        reset,
        handleSubmit,
        formState: { errors },
    } = methods;
    const { fields, append, remove } = useFieldArray({ name: 'bil', control });
    const { mutate: saveBiler, isPending: isSavingBiler } = useUpdateBil(bilContainer, () => {
        navigate(navigateTo);
    });
    const deleteBil = useDeleteBil();
    const harBiler = useWatch({ name: 'harBil', control });

    const innhentedeBiler = bilContainer?.biler?.filter((bil) => !bil.erLagtTilManuelt);

    useEffect(() => {
        if (bilContainer) {
            const biler: BilFormData = { harBil: bilContainer.harBiler || ValgtWeb.IKKE_VALGT, bil: [] };
            bilContainer.biler
                .filter((bil) => bil.erLagtTilManuelt)
                ?.forEach((b) => {
                    biler.bil.push({
                        bilId: b.id,
                        drivstoff: b.drivstoff || DrivstoffWeb.UKJENT,
                        erLeaset: b.leasingUtgift ? ValgtWeb.JA : ValgtWeb.NEI,
                        leasingUtgift: b.leasingUtgift,
                        erLagtTilManuelt: true,
                        eier: null,
                        merke: null,
                        modell: null,
                        registreringsnummer: null,
                        registrertFoersteGangNorge: null,
                    });
                });

            bilContainer?.biler
                ?.filter((bil) => !bil.erLagtTilManuelt && bil.erLeasing)
                ?.forEach((b) => {
                    biler.bil.push({
                        bilId: b.id,
                        drivstoff: b.drivstoff || DrivstoffWeb.UKJENT,
                        erLeaset: b.erLeasing ? ValgtWeb.JA : ValgtWeb.NEI,
                        leasingUtgift: b.leasingUtgift,
                        erLagtTilManuelt: false,
                        eier: b.eier,
                        merke: b.merke,
                        modell: b.modell,
                        registreringsnummer: b.registreringsnummer,
                        registrertFoersteGangNorge: b.registrertFoersteGangNorge,
                    });
                });

            reset(biler);
        }
    }, [bilContainer, reset]);

    const onSumbitBil = (data: BilFormData) => {
        if (bilContainer) {
            const biler: BilWeb[] = [];
            data.bil.forEach((b1) => {
                const bil = bilContainer.biler.find((b2) => b2.id !== '' && b2.id === b1.bilId);
                if (bil) {
                    bil.id = bil.id || '';
                    bil.drivstoff = b1.drivstoff;
                    bil.leasingUtgift = b1.leasingUtgift || 0;
                    biler.push(bil);
                } else {
                    biler.push({
                        id: '',
                        drivstoff: b1.drivstoff,
                        leasingUtgift: b1.leasingUtgift || 0,
                        status: StatusWeb.CONFIRMED,
                        merke: b1.merke,
                        modell: b1.modell,
                        registrertFoersteGangNorge: b1.registrertFoersteGangNorge,
                        registreringsnummer: b1.registreringsnummer,
                        eier: b1.eier,
                        erLeasing: b1.erLeaset === ValgtWeb.JA,
                        erLagtTilManuelt: b1.erLagtTilManuelt,
                    });
                }
            });
            innhentedeBiler
                ?.filter((bil) => !bil.erLeasing)
                .forEach((bil) => {
                    bil.id = bil.id || '';
                    biler.push(bil);
                });

            saveBiler({ soeknadId: bilContainer.soeknadId, biler });
        } else {
            navigate(navigateTo);
        }
    };

    const onClickLeggTilBil = () => {
        append(newBil);
    };

    const onClickHarBil = (valg: ValgtWeb) => {
        if (valg === ValgtWeb.JA && fields.length === 0) {
            append(newBil);
        } else if (valg === ValgtWeb.NEI) {
            const bilIder: string[] = [];
            fields.forEach((f) => {
                if (f.bilId !== null) {
                    bilIder.push(f.bilId);
                }
            });
            if (bilContainer?.soeknadId && bilIder.length > 0) {
                bilIder.forEach((id) => {
                    deleteBil.mutate(
                        { bilId: id, soeknadId: bilContainer.soeknadId },
                        {
                            onSuccess: () => {
                                remove();
                            },
                        },
                    );
                });
            } else {
                remove();
            }
        }
    };

    const onClickSlett = (index: number) => {
        const id = fields[index].bilId;
        if (fields.length === 1) {
            setValue('harBil', ValgtWeb.NEI, { shouldValidate: true });
        }
        if (id && bilContainer?.soeknadId) {
            deleteBil.mutate(
                { bilId: id, soeknadId: bilContainer.soeknadId },
                {
                    onSuccess: () => {
                        remove(index);
                    },
                },
            );
        } else {
            remove(index);
        }
    };

    const skalViseValg = harBiler === ValgtWeb.JA || fields.length > 0;

    return (
        <PageCard title={utvideDatagrunnlag ? biler.title : biler.titleHusholdning}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSumbitBil)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">
                            {utvideDatagrunnlag ? biler.subTitle : biler.subTitleHusholdning}
                        </span>
                        {utvideDatagrunnlag && <span className="typography-ingress__light">{biler.subTitleNb}</span>}
                        {isPending ? (
                            <Loader />
                        ) : (
                            <>
                                {innhentedeBiler?.map((bil) => {
                                    const index = fields.findIndex(
                                        (f) => f.registreringsnummer === bil.registreringsnummer,
                                    );
                                    return (
                                        <Kjoeretoey
                                            key={bil.registreringsnummer}
                                            index={index}
                                            kjoeretoey={bil}
                                            register={register}
                                            control={control}
                                            errors={errors}
                                        />
                                    );
                                })}
                                {!(innhentedeBiler && innhentedeBiler.length > 0) && (
                                    <BilerValg onClickHarBil={onClickHarBil} featureToggle={utvideDatagrunnlag} />
                                )}
                                {skalViseValg &&
                                    fields.map((field, index) => {
                                        return field.erLagtTilManuelt ? (
                                            <div
                                                key={field.id}
                                                className="form-box-seagreen row-gap margin-bottom-small"
                                            >
                                                <Drivstoff
                                                    index={index}
                                                    register={register}
                                                    control={control}
                                                    setValue={setValue}
                                                    errors={errors}
                                                />
                                                <Leaset
                                                    index={index}
                                                    register={register}
                                                    control={control}
                                                    errors={errors}
                                                />
                                                <div className="column">
                                                    <LinkButton
                                                        icon={IconTypes.SLETT_24}
                                                        data-track-id="kreditt-boliglaan-bil-slett-ikon"
                                                        data-track-linkname="fjern"
                                                        onClick={() => onClickSlett(index)}
                                                    >
                                                        {button.fjern}
                                                    </LinkButton>
                                                </div>
                                            </div>
                                        ) : null;
                                    })}
                                {(skalViseValg || (innhentedeBiler && innhentedeBiler.length > 0)) && (
                                    <div className="column">
                                        <StandardButton
                                            icon={IconTypes.PLUSS_36}
                                            buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                            onClick={onClickLeggTilBil}
                                        >
                                            {button.leggTilBil}
                                        </StandardButton>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <PageFooter
                        soeknadId={bilContainer?.soeknadId}
                        onForrigeClick={() => navigate(navigateFrom)}
                        showSpinner={isSavingBiler || isPending}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Biler;
